/**
 * Chilean Phone Number Validation Utilities
 * 
 * Validates and formats Chilean mobile phone numbers.
 * Chilean mobile format: +56 9 XXXX XXXX (9 digits after country code)
 * - Country code: +56
 * - Mobile prefix: 9
 * - Number: 8 digits (XXXX XXXX)
 */

/**
 * Formats a Chilean phone number to the standard format: +56 9 XXXX XXXX
 * 
 * @param {string} phone - The phone number to format (with or without +56 prefix)
 * @returns {string} The formatted phone or empty string if invalid/empty
 */
export function formatChileanPhone(phone) {
  if (!phone || typeof phone !== 'string') {
    return '';
  }
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle empty input
  if (digits.length === 0) {
    return '';
  }
  
  let normalizedDigits = digits;
  
  // Remove country code if present (56)
  if (normalizedDigits.startsWith('56') && normalizedDigits.length >= 11) {
    normalizedDigits = normalizedDigits.slice(2);
  }
  
  // Must have exactly 9 digits (9 + 8 digit number)
  if (normalizedDigits.length !== 9) {
    return '';
  }
  
  // Must start with 9 (mobile prefix)
  if (!normalizedDigits.startsWith('9')) {
    return '';
  }
  
  // Format as +56 9 XXXX XXXX
  const part1 = normalizedDigits.slice(1, 5);
  const part2 = normalizedDigits.slice(5, 9);
  
  return `+56 9 ${part1} ${part2}`;
}

/**
 * Validates a Chilean mobile phone number
 * 
 * @param {string} phone - The phone number to validate (with or without +56 prefix)
 * @returns {Object} Validation result with isValid, formatted, and error fields
 * @returns {boolean} result.isValid - Whether the phone is valid
 * @returns {string} result.formatted - The formatted phone (+56 9 XXXX XXXX) or empty string
 * @returns {string} [result.error] - Error message in Spanish if invalid
 */
export function validateChileanPhone(phone) {
  // Empty string is valid (phone is optional)
  if (!phone || (typeof phone === 'string' && phone.trim() === '')) {
    return {
      isValid: true,
      formatted: '',
      error: undefined
    };
  }
  
  if (typeof phone !== 'string') {
    return {
      isValid: false,
      formatted: '',
      error: 'Ingresa un número chileno válido (+56 9 XXXX XXXX)'
    };
  }
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle empty after cleaning
  if (digits.length === 0) {
    return {
      isValid: true,
      formatted: '',
      error: undefined
    };
  }
  
  let normalizedDigits = digits;
  
  // Remove country code if present (56)
  if (normalizedDigits.startsWith('56') && normalizedDigits.length >= 11) {
    normalizedDigits = normalizedDigits.slice(2);
  }
  
  // Must have exactly 9 digits (9 + 8 digit number)
  if (normalizedDigits.length !== 9) {
    return {
      isValid: false,
      formatted: '',
      error: 'Ingresa un número chileno válido (+56 9 XXXX XXXX)'
    };
  }
  
  // Must start with 9 (mobile prefix)
  if (!normalizedDigits.startsWith('9')) {
    return {
      isValid: false,
      formatted: '',
      error: 'Ingresa un número chileno válido (+56 9 XXXX XXXX)'
    };
  }
  
  // Format the valid phone
  const formatted = formatChileanPhone(phone);
  
  return {
    isValid: true,
    formatted,
    error: undefined
  };
}

export default {
  validateChileanPhone,
  formatChileanPhone
};
